import { useColorModeValue, useTheme } from "@chakra-ui/react";
import clsx from "clsx";
import TimezoneSelect, { Props as TimezoneSelectProps } from "react-timezone-select";
import { Variant } from "chakra-react-select";
import { CustomOption, CustomSingleValue } from "./CustomComponent";

interface BaseTimezoneSelectProps extends TimezoneSelectProps {
  height?: string;
  width?: string;
  variant?: Variant;
  size?: "sm" | "md";
}

export default function BaseTimezoneButtonSelect({
  size,
  className,
  variant,
  placeholder,
  height,
  width,
  styles,
  ...rest
}: BaseTimezoneSelectProps) {
  const isSmall = size === "sm";
  const menuBg = useColorModeValue("menu.bg", "var(--chakra-colors-gray-700)");
  const optionBg = useColorModeValue("card.bg", "transparent");
  const optionFocusBackground = useColorModeValue(
    "var(--chakra-colors-gray-100)",
    "var(--chakra-colors-whiteAlpha-100)",
  );

  const chakraTheme = useTheme();
  const mergedClassName = clsx(
    `timezone ${isSmall ? "is-small" : ""} chakra-react-select-container-single-select`,
    className,
  );

  return (
    <TimezoneSelect
      menuPortalTarget={document.body}
      labelStyle="offsetHidden"
      className={mergedClassName}
      classNamePrefix="chakra-react-select"
      placeholder={(variant as string) === "floating" ? " " : placeholder}
      styles={{
        container: (provided) => ({
          ...provided,
          minWidth: "200px",
          width: width ?? "100%",
        }),
        option: (provided, state) => ({
          ...provided,
          color: "var(--chakra-colors-fg-muted)",
          backgroundColor: state.isFocused ? optionFocusBackground : menuBg,
          cursor: "pointer",
          fontSize: isSmall ? "14px" : provided.fontSize,
        }),
        control: (provided) => ({
          ...provided,
          ...chakraTheme.components.Input.baseStyle.field,
          boxShadow: "none",
          cursor: "pointer",
          backgroundColor: optionBg,
          fontSize: isSmall ? "14px" : provided.fontSize,
          minHeight: isSmall ? "32px" : provided.minHeight,
          padding: isSmall ? "4px 4px 4px 2px" : provided.padding,
          maxHeight: isSmall ? "32px" : provided.maxHeight,
          borderRadius: "var(--chakra-radii-lg)",
        }),
        valueContainer: (provided) => ({
          ...provided,
          padding: size === "sm" ? "2px 4px" : "2px 8px 2px 0px",
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          padding: size === "sm" ? "2px" : provided.padding,
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: size === "sm" ? "2px" : "4px",
          width: "var(--chakra-sizes-6)",
          marginRight: "var(--chakra-space-2)",
          marginLeft: "var(--chakra-space-1)",
          svg: {
            width: size === "sm" ? "14px" : "1em",
            height: size === "sm" ? "14px" : "1em",
            display: "inline-block",
            lineHeight: "1em",
            color: "rgb(74, 85, 104)",
            verticalAlign: "middle",
          },
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        singleValue: (provided) => ({
          ...provided,
          color: "var(--chakra-colors-fg-muted)",
          backgroundColor: "transparent",
          paddingTop: size === "sm" ? "5px !important" : provided.paddingTop,
          paddingLeft: size === "sm" ? "0px !important" : provided.paddingLeft,
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 1500 }),
        menuList: (provided) => ({ ...provided, background: menuBg }),
        menu: (provided) => ({
          ...provided,
          fontSize: isSmall ? "14px" : provided.fontSize,
          cursor: "pointer",
          background: menuBg,
        }),
        ...styles,
      }}
      components={{
        SingleValue: CustomSingleValue,
        Option: CustomOption,
      }}
      {...rest}
    />
  );
}
